import { useState } from 'react';
import { t } from 'i18next';
import { BankProductsResponse, BankProduct } from '@modules/banks/api/types';
import Table from '@components/v2/Table';
import {
  Box,
  Flex,
  Text,
} from '@huspy/briks-web';
import { BankProductDetailsModal } from '@modules/banks/features/BankProducts/BankProductDetails';
import { ReportProblemModal } from '@modules/banks/features/ReportProblem';
import Tooltip from '@components/Tooltip';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import {
  getInterestRate, getInterestRateDescription, getFollowOnRate, getFollowOnRateDescription
} from '@modules/banks/utils';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { BANK_RATE_STATUS } from '@modules/banks/api/const';
import { AMPLITUDE_FEATURE_FLAGS } from '@modules/core/api/types';
import { useGetBaseRates } from '@modules/banks/hooks/queries/useGetBaseRates';
import CurrencyFormatter from '@components/CurrencyFormatter';
import BankIcon from '@components/v2/BankIcon';
import { Tag } from '@modules/banks/presentation/v2/features/BankProducts/components/Tag';
import { bankProductsStyles } from '@modules/banks/presentation/v2/features/BankProducts/BankProductsTable/styles/index.css';
import { ProductMetaInfo } from '@modules/banks/presentation/v2/features/BankProducts/components/ProductMetaInfo';
import { Checkbox } from '@mantine/core';

const { bankIconPlaceholder, hideOnSmallScreen } = bankProductsStyles;

const TableHeaderCell = (
  { children, width, isSimplifiedView }:
  { children: any, width?: string, isSimplifiedView?: boolean }
) => (
  <Table.Header>
    <Flex width={ width ?? '100%' } p={ isSimplifiedView ? 2 : 4 } py={ 4 }>
      <Box style={ { whiteSpace: 'nowrap' } }>
        <Text fontWeight='normal' size={ isSimplifiedView ? 'sm' : 'md' }>{children}</Text>
      </Box>
    </Flex>
  </Table.Header>
);

const TableContent = ({
  data,
  selectedProducts,
  handleOnChange,
  maxProductsSelection,
  showCheckboxes,
  showMonthlyPayment,
  onProductClick,
  isSimplifiedView,
}: {
  data: BankProduct[],
  selectedProducts?: BankProduct[],
  handleOnChange?: Function,
  maxProductsSelection?: number,
  showCheckboxes?: boolean,
  showMonthlyPayment?: boolean,
  onProductClick?: Function,
  isSimplifiedView?: boolean,
// eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { featureFlags } = useFeatureFlags();
  const { data: baseRates } = useGetBaseRates();
  const isProductSelected = (product: BankProduct) => {
    if (!selectedProducts) return;
    const foundSelectedProduct = selectedProducts.find((_product) => _product.id === product.id);
    return !!foundSelectedProduct;
  };

  const cellPaddings = isSimplifiedView ? { px: 2, py: 3 } : undefined;

  const isCheckboxDisabled = (product: BankProduct) => {
    if (
      maxProductsSelection === undefined
      || !selectedProducts
      || isProductSelected(product)
    ) return false;

    if (product.status_type !== BANK_RATE_STATUS.ACTIVE) return true;

    return selectedProducts.length >= maxProductsSelection;
  };

  const handleOnCheckedChange = (product: BankProduct, checked: boolean) => {
    if (handleOnChange) handleOnChange(product, checked);
  };

  const openProductDetails = (product: BankProduct | null) => {
    if (onProductClick) {
      onProductClick(product);
      trackAmplitudeEvent(USER_EVENTS.BANK_PRODUCTS.DETAILS_VIEWED);
    }
  };

  return data.map((product) => (
    <Table.Row
      key={ product.id }
      data-test={ `bank-product-row-${product.id}` }
      data-testid={ `bank-product-row-${product.id}` }
      onClick={ () => openProductDetails(product) }
      style={ { cursor: 'pointer' } }
    >
      {/* Checkbox */}
      {
        showCheckboxes && (
          <Table.Cell
            { ...({
              style: { cursor: 'auto' },
              onClick: (e) => e.stopPropagation(),
            }) }
            { ...cellPaddings }
          >
            <Flex direction='column' align='center' gap='2'>
              <Checkbox
                checked={ isProductSelected(product) }
                onChange={ (e) => handleOnCheckedChange(product, !!e.target.checked) }
                disabled={ isCheckboxDisabled(product) }
              />
            </Flex>
          </Table.Cell>
        )
      }

      <Table.Cell { ...cellPaddings }>
        <Tooltip label={ product.bank_name }>
          <Flex justify='center' align='center'>
            <Flex className={ bankIconPlaceholder }>
              <BankIcon
                icon={ product.icon }
                hasShadow
                style={ { height: '100%', width: '100%' } }
              />
            </Flex>
          </Flex>
        </Tooltip>
      </Table.Cell>

      {/* Product info */}
      <Table.Cell { ...cellPaddings }>
        <ProductMetaInfo product={ product } />
      </Table.Cell>

      {
        isSimplifiedView ? (
          <Table.Cell { ...cellPaddings }>
            <Flex direction='column' align='flex-start' gap={ 1.5 }>
              <Flex justifyContent='center'>
                <Tag name={ product.type_of_mortgage } />
              </Flex>

              <Flex justifyContent='center'>
                <Tag name={ product.type_of_transaction } />
              </Flex>
            </Flex>
          </Table.Cell>
        ) : (
          <>
            {/* Mortgage type */}
            <Table.Cell { ...cellPaddings }>
              <Flex direction='column'>
                <Flex>
                  <Tag name={ product.type_of_mortgage } />
                </Flex>
              </Flex>
            </Table.Cell>

            {/* Transaction type */}
            <Table.Cell { ...cellPaddings }>
              <Flex direction='column'>
                <Flex>
                  <Tag name={ product.type_of_transaction } />
                </Flex>
              </Flex>
            </Table.Cell>
          </>
        )
      }

      {/* Interest rate */}
      <Table.Cell { ...cellPaddings }>
        <Flex gap='1' direction='row'>
          <Text fontWeight='medium' size='md'>
            { getInterestRate(product) }
            %
          </Text>
          <Text color='text-tertiary' fontWeight='light' size='md'>
            { getInterestRateDescription(product, baseRates!, true) }
          </Text>
        </Flex>
      </Table.Cell>

      {/* Bank fees */}
      <Table.Cell { ...cellPaddings }>
        <Flex>
          { product.mortgage_processing_fee }
          %
        </Flex>
      </Table.Cell>

      {/* Follow on rate */}
      <Table.Cell { ...cellPaddings }>
        <Flex gap='1' direction='row'>
          <Text fontWeight='medium' size='md'>
            { getFollowOnRate(product) }
            %
          </Text>
          <Text color='text-tertiary' fontWeight='light' size='md'>
            { getFollowOnRateDescription(product, baseRates!, true) }
          </Text>
        </Flex>
      </Table.Cell>

      {/* Monthly payment */}
      { showMonthlyPayment
        && (
          <Table.Cell { ...cellPaddings }>
            <Flex>
              <CurrencyFormatter currency='AED' value={ product.monthly_payment } />
            </Flex>
          </Table.Cell>
        )}

      {/* Fee Financing */}
      {
        featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_BANK_PRODUCTS_SHOW_FEE_FINANCING_COLUMN]
        && (
          <Table.Cell className={ hideOnSmallScreen } { ...cellPaddings }>
            <Flex direction='column'>
              {
                (!product.includes_agency_fees && !product.includes_government_fees)
              && (
                <Text size='md' color={ isSimplifiedView ? 'text-tertiary' : 'text-primary' }>
                  { t('bankProducts.table.v2.no') }
                </Text>
              )
              }
              {
                (product.includes_agency_fees || product.includes_government_fees)
              && (
                <Text size='md' color={ isSimplifiedView ? 'text-tertiary' : 'text-primary' }>
                  { t('bankProducts.table.v2.yes') }
                </Text>
              )
              }
            </Flex>
          </Table.Cell>
        )
      }
    </Table.Row>
  ));
};

export const BankProductsTable = ({
  data,
  isLoading,
  handleOnChange,
  maxProductsSelection,
  selectedBankProducts,
  isSimplifiedView,
}: {
  data?: BankProductsResponse,
  isLoading: boolean,
  handleOnChange?: Function,
  maxProductsSelection?: number,
  selectedBankProducts?: BankProduct[],
  isSimplifiedView?: boolean,
}) => {
  const { featureFlags } = useFeatureFlags();
  const [productDetailsOpened, setProductDetailsOpened] = useState<BankProduct | null>(null);
  const [reportProblemOpened, setReportProblemOpened] = useState(false);

  const handleOnCheckedChange = (product: BankProduct, checked: boolean) => {
    if (!selectedBankProducts) return;

    let newProductSelection: BankProduct[] = [];

    if (checked) {
      newProductSelection = [...selectedBankProducts, product];
    }

    if (!checked) {
      newProductSelection = selectedBankProducts.filter((_product) => _product.id !== product.id);
    }

    if (handleOnChange) handleOnChange(newProductSelection);
  };

  const isBankProductSelection = !!handleOnChange;
  const showCheckboxes = isBankProductSelection;
  const showMonthlyPayment = isBankProductSelection;

  return (
    <>
      <BankProductDetailsModal
        product={ productDetailsOpened }
        open={ !!productDetailsOpened }
        onClose={ () => setProductDetailsOpened(null) }
        onReportProblemClick={ () => {
          setReportProblemOpened(true);
          trackAmplitudeEvent(USER_EVENTS.BANK_PRODUCTS.REPORT_A_PROBLEM_CLICKED);
        } }
      />

      <ReportProblemModal
        product={ productDetailsOpened }
        open={ reportProblemOpened }
        onClose={ () => setReportProblemOpened(false) }
        onReportProblemSubmit={ () => {
          setReportProblemOpened(false);
          trackAmplitudeEvent(USER_EVENTS.BANK_PRODUCTS.REPORT_A_PROBLEM_SENT);
        } }
      />

      <Table.Root style={ { tableLayout: 'auto' } }>
        <Table.Head>
          <Table.Row>
            {
              showCheckboxes
            && (
              <TableHeaderCell
                isSimplifiedView={ isSimplifiedView }
                width='auto'
              >
                <div />
              </TableHeaderCell>
            )
            }
            <TableHeaderCell
              isSimplifiedView={ isSimplifiedView }
              width='auto'
            >
              { t('bankProducts.table.v2.bank') }
            </TableHeaderCell>
            <TableHeaderCell
              isSimplifiedView={ isSimplifiedView }
              width={ isSimplifiedView ? '160px' : 'auto' }
            >
              { t('bankProducts.table.v2.productInfo') }
            </TableHeaderCell>
            {
              isSimplifiedView ? (
                <TableHeaderCell
                  isSimplifiedView={ isSimplifiedView }
                  width='auto'
                >
                  { t('bankProducts.table.v2.mortgageTransactionType') }
                </TableHeaderCell>
              ) : (
                <>
                  <TableHeaderCell
                    isSimplifiedView={ isSimplifiedView }
                    width='auto'
                  >
                    { t('bankProducts.table.v2.mortgageType') }
                  </TableHeaderCell>
                  <TableHeaderCell
                    isSimplifiedView={ isSimplifiedView }
                    width='auto'
                  >
                    { t('bankProducts.table.v2.transactionType') }
                  </TableHeaderCell>
                </>
              )
            }

            <TableHeaderCell
              isSimplifiedView={ isSimplifiedView }
              width='auto'
            >
              { t('bankProducts.table.v2.interestRate') }
            </TableHeaderCell>
            <TableHeaderCell
              isSimplifiedView={ isSimplifiedView }
              width='auto'
            >
              { t('bankProducts.table.v2.bankFees') }
            </TableHeaderCell>
            <TableHeaderCell
              isSimplifiedView={ isSimplifiedView }
              width='auto'
            >
              { t('bankProducts.table.v2.followOnRate') }
            </TableHeaderCell>
            {
              isBankProductSelection
            && (
              <TableHeaderCell
                isSimplifiedView={ isSimplifiedView }
                width='auto'
              >
                { t('bankProducts.table.v2.monthlyPayment') }
              </TableHeaderCell>
            )
            }
            {
              featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_BANK_PRODUCTS_SHOW_FEE_FINANCING_COLUMN]
            && (
              <TableHeaderCell
                isSimplifiedView={ isSimplifiedView }
                width='auto'
              >
                { t('bankProducts.table.v2.feeFinancing') }
              </TableHeaderCell>
            )
            }
          </Table.Row>
        </Table.Head>
        <Table.Body>
          { isLoading || !data
            ? <Table.Loading rows={ 10 } cells={ showMonthlyPayment ? 9 : 8 } />
            : (
              <TableContent
                data={ data.results }
                selectedProducts={ selectedBankProducts ?? [] }
                handleOnChange={ handleOnCheckedChange }
                maxProductsSelection={ maxProductsSelection }
                showCheckboxes={ showCheckboxes }
                showMonthlyPayment={ showMonthlyPayment }
                onProductClick={ setProductDetailsOpened }
                isSimplifiedView={ isSimplifiedView }
              />
            ) }
        </Table.Body>
      </Table.Root>
    </>

  );
};
